import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import Cookies from 'js-cookie'
import { AppCookie } from 'utils/cookies'

dayjs.extend(timezone)
dayjs.extend(utc)

/**
 * utc: always UTC timezone
 * local: always user local timezone
 * partnerTimezone: partner's timezone from cookies, otherwise local
 */
export type ITypes = 'utc' | 'local' | 'partnerTimezone'

export const getTimezoneOffset = (type: ITypes): string => {
  if (type === 'utc') {
    return ''
  }

  let currentDate = dayjs()

  const partnerTimezone = Cookies.get(AppCookie.PartnerTimezone)
  if (partnerTimezone && type === 'partnerTimezone') {
    currentDate = currentDate.tz(partnerTimezone)
  }

  const timezone = currentDate.format('Z')

  const timezoneOffset = timezone.slice(0, 3).replace(/^([+-])0/, '$1')
  if (timezoneOffset === '+0') {
    return ''
  }

  return timezoneOffset
}

/**
 * utc - date (2025-05-14 00:00:00+00)
 * example: dueDate, dateOfBirth
 *
 * local - datetime (2025-05-14 15:30:23.670862+00)
 * example: createdAt, sentAt
 */
export const formatDate = (
  date: string | Date,
  format: string,
  type: ITypes,
  showTimezone: boolean = false
): string => {
  if (!date) return ''

  let parsedDate: Date
  if (typeof date === 'string') {
    parsedDate = new Date(date)
  } else if (date instanceof Date) {
    parsedDate = date
  }

  if (isNaN(parsedDate.getTime())) {
    return ''
  }

  let convertedDate = dayjs(parsedDate)
  if (type === 'utc') {
    convertedDate = convertedDate.utc()
  }

  const partnerTimezone = Cookies.get(AppCookie.PartnerTimezone)
  if (partnerTimezone && type === 'partnerTimezone') {
    convertedDate = convertedDate.tz(partnerTimezone)
  }

  const formattedDate = convertedDate.format(format)

  if (showTimezone) {
    const timezoneOffset = getTimezoneOffset(type)
    return `${formattedDate} (UTC${timezoneOffset})`
  }

  return formattedDate
}

export const formatTimezoneOption = (
  timezone: string,
  countryPrefix: string
) => {
  const offset = dayjs().tz(timezone).format('Z')
  const formattedTimezone = timezone
    .replace(/.*?\//, `${countryPrefix}/`) // replace all characters before the first / with the country prefix
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return {
    label: `${formattedTimezone} (UTC${offset})`,
    value: TimezoneToGQLEnumValue[timezone]
  }
}

export const canadaTimezones = [
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Blanc-Sablon',
  'America/Toronto',
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Iqaluit',
  'America/Pangnirtung',
  'America/Resolute',
  'America/Atikokan',
  'America/Rankin_Inlet',
  'America/Winnipeg',
  'America/Rainy_River',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Yellowknife',
  'America/Inuvik',
  'America/Creston',
  'America/Dawson_Creek',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Dawson'
]

export const usaTimezones = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'America/Metlakatla',
  'Pacific/Honolulu'
]

export const TimezoneToGQLEnumValue = {
  UTC: 'UTC',
  'America/St_Johns': 'AMERICA_ST_JOHNS',
  'America/Halifax': 'AMERICA_HALIFAX',
  'America/Glace_Bay': 'AMERICA_GLACE_BAY',
  'America/Moncton': 'AMERICA_MONCTON',
  'America/Goose_Bay': 'AMERICA_GOOSE_BAY',
  'America/Blanc-Sablon': 'AMERICA_BLANC_SABLON',
  'America/Toronto': 'AMERICA_TORONTO',
  'America/Nipigon': 'AMERICA_NIPIGON',
  'America/Thunder_Bay': 'AMERICA_THUNDER_BAY',
  'America/Iqaluit': 'AMERICA_IQALUIT',
  'America/Pangnirtung': 'AMERICA_PANGNIRTUNG',
  'America/Resolute': 'AMERICA_RESOLUTE',
  'America/Atikokan': 'AMERICA_ATIKOKAN',
  'America/Rankin_Inlet': 'AMERICA_RANKIN_INLET',
  'America/Winnipeg': 'AMERICA_WINNIPEG',
  'America/Rainy_River': 'AMERICA_RAINY_RIVER',
  'America/Regina': 'AMERICA_REGINA',
  'America/Swift_Current': 'AMERICA_SWIFT_CURRENT',
  'America/Edmonton': 'AMERICA_EDMONTON',
  'America/Cambridge_Bay': 'AMERICA_CAMBRIDGE_BAY',
  'America/Yellowknife': 'AMERICA_YELLOWKNIFE',
  'America/Inuvik': 'AMERICA_INUVIK',
  'America/Creston': 'AMERICA_CRESTON',
  'America/Dawson_Creek': 'AMERICA_DAWSON_CREEK',
  'America/Vancouver': 'AMERICA_VANCOUVER',
  'America/Whitehorse': 'AMERICA_WHITEHORSE',
  'America/Dawson': 'AMERICA_DAWSON',
  'America/New_York': 'AMERICA_NEW_YORK',
  'America/Detroit': 'AMERICA_DETROIT',
  'America/Kentucky/Louisville': 'AMERICA_KENTUCKY_LOUISVILLE',
  'America/Kentucky/Monticello': 'AMERICA_KENTUCKY_MONTICELLO',
  'America/Indiana/Indianapolis': 'AMERICA_INDIANA_INDIANAPOLIS',
  'America/Indiana/Vincennes': 'AMERICA_INDIANA_VINCENNES',
  'America/Indiana/Winamac': 'AMERICA_INDIANA_WINAMAC',
  'America/Indiana/Marengo': 'AMERICA_INDIANA_MARENGO',
  'America/Indiana/Petersburg': 'AMERICA_INDIANA_PETERSBURG',
  'America/Indiana/Vevay': 'AMERICA_INDIANA_VEVAY',
  'America/Chicago': 'AMERICA_CHICAGO',
  'America/Indiana/Tell_City': 'AMERICA_INDIANA_TELL_CITY',
  'America/Indiana/Knox': 'AMERICA_INDIANA_KNOX',
  'America/Menominee': 'AMERICA_MENOMINEE',
  'America/North_Dakota/Center': 'AMERICA_NORTH_DAKOTA_CENTER',
  'America/North_Dakota/New_Salem': 'AMERICA_NORTH_DAKOTA_NEW_SALEM',
  'America/North_Dakota/Beulah': 'AMERICA_NORTH_DAKOTA_BEULAH',
  'America/Denver': 'AMERICA_DENVER',
  'America/Boise': 'AMERICA_BOISE',
  'America/Phoenix': 'AMERICA_PHOENIX',
  'America/Los_Angeles': 'AMERICA_LOS_ANGELES',
  'America/Anchorage': 'AMERICA_ANCHORAGE',
  'America/Juneau': 'AMERICA_JUNEAU',
  'America/Sitka': 'AMERICA_SITKA',
  'America/Yakutat': 'AMERICA_YAKUTAT',
  'America/Nome': 'AMERICA_NOME',
  'America/Adak': 'AMERICA_ADAK',
  'America/Metlakatla': 'AMERICA_METLAKATLA',
  'Pacific/Honolulu': 'PACIFIC_HONOLULU'
}
